import { useEffect, useMemo, useRef, useState } from "react";
import "./theme.css";
import "./FtaCenterPage.css";
import { resolveFtaByCode } from "./ftaAgreements";

/**
 * FTA 센터(문의 #125, 2026-10-02) — 협정 목록 · 한눈에 비교 · 협정 상세 한 장 · 원산지신고 문구 만들기.
 * 자료: GET /api/onto/fta-center (data/fta_agreements.json, 생성기 scripts/generate-fta-agreements.py).
 * 값은 관세청 FTA 포털·국가법령정보센터 원본 표에서 뽑은 것이고, 화면 문장은 우리 말로 쓴다. 판정에 쓰지 않는다.
 */

let cache = null;
export function loadFtaCenter() {
  if (!cache) {
    cache = fetch("/api/onto/fta-center")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .catch((e) => {
        cache = null;
        throw e;
      });
  }
  return cache;
}

/** 세율부호(FEU1·FRCJP2·FCECR1…) → 협정 id. 긴 앞자리 먼저. */
export function agreementIdForRate(data, code) {
  const c = String(code || "").toUpperCase();
  let best = null;
  for (const a of data?.agreements || []) {
    for (const p of a.rate_prefixes || []) {
      if (c.startsWith(p) && (!best || p.length > best.len)) best = { id: a.id, len: p.length };
    }
  }
  return best ? best.id : null;
}

let BASE = { key: "audit", value: "fta" };
function setBase(publicMode) {
  BASE = publicMode ? { key: "page", value: "fta" } : { key: "audit", value: "fta" };
}

function readRoute() {
  const p = new URLSearchParams(window.location.search);
  const hsk = (p.get("hsk") || "").replace(/\D/g, "");
  return {
    ag: p.get("ag") || null,
    view: p.get("view") === "compare" ? "compare" : "list",
    hsk: hsk.length === 10 ? hsk : null,
    code: p.get("code") || null,
  };
}

/** ag(협정 id) 또는 code(세율부호 — 화면이 협정으로 바꾼다) · hsk(세번에서 넘어온 경우) */
export function ftaHref({ ag = null, view = null, hsk = null, code = null } = {}) {
  const p = new URLSearchParams();
  p.set(BASE.key, BASE.value);
  if (ag) p.set("ag", ag);
  if (code && !ag) p.set("code", code);
  if (hsk) p.set("hsk", hsk);
  if (code && ag) p.set("code", code);
  if (view && !ag && !code) p.set("view", view);
  return "/?" + p.toString();
}

function go(target) {
  window.history.pushState({}, "", ftaHref(target));
  window.dispatchEvent(new PopStateEvent("popstate"));
}

const fmtDate = (s) => (s ? s.replace(/-/g, ".") : "—");
const isSelf = (m) => /자율/.test(m || "");
const isInst = (m) => /기관/.test(m || "");

function MethodBadges({ method }) {
  return (
    <span className="fta-badges">
      {isInst(method) && <span className="fta-badge">기관발급</span>}
      {isSelf(method) && <span className="fta-badge fta-badge-self">자율발급</span>}
    </span>
  );
}

function membersShort(a) {
  const m = a.members || [];
  if (m.length <= 1) return m[0] || "";
  if (m.length <= 4) return m.join(" · ");
  return `${m.slice(0, 3).join(" · ")} 외 ${m.length - 3}`;
}

function AgreementList({ data, q }) {
  const list = useMemo(() => {
    const n = q.trim();
    const all = [...(data.agreements || [])].sort((a, b) => (b.dates?.effective || "").localeCompare(a.dates?.effective || ""));
    if (!n) return all;
    return all.filter((a) => `${a.name} ${(a.members || []).join(" ")} ${(a.rate_prefixes || []).join(" ")}`.toLowerCase().includes(n.toLowerCase()));
  }, [data, q]);
  return (
    <div className="fta-grid" data-testid="fta-list">
      {list.map((a) => (
        <button key={a.id} type="button" className="fta-card" onClick={() => go({ ag: a.id })}>
          <div className="fta-card-head">
            <span className="fta-card-name">{a.name}</span>
            <span className="fta-card-date">{fmtDate(a.dates?.effective)} 발효</span>
          </div>
          <div className="fta-card-members">{membersShort(a)}</div>
          <div className="fta-card-foot">
            <MethodBadges method={a.proof?.method} />
            <span className="fta-card-valid">유효 {a.proof?.validity || "—"}</span>
          </div>
        </button>
      ))}
      {list.length === 0 && <div className="fta-empty">검색어와 맞는 협정이 없습니다.</div>}
    </div>
  );
}

const COLS = [
  { key: "name", label: "협정", get: (a) => a.name },
  { key: "effective", label: "발효", get: (a) => a.dates?.effective || "" },
  { key: "method", label: "발급방식", get: (a) => a.proof?.method || "" },
  { key: "issuer", label: "발급자", get: (a) => a.proof?.issuer || "" },
  { key: "form", label: "서식", get: (a) => a.proof?.form || "" },
  { key: "validity", label: "유효기간", get: (a) => a.proof?.validity || "" },
  { key: "language", label: "사용언어", get: (a) => a.proof?.language || "" },
];

function CompareTable({ data, q }) {
  const [sort, setSort] = useState({ key: "effective", dir: -1 });
  const [only, setOnly] = useState("all");
  const rows = useMemo(() => {
    const col = COLS.find((c) => c.key === sort.key) || COLS[1];
    const n = q.trim().toLowerCase();
    return [...(data.agreements || [])]
      .filter((a) => (only === "self" ? isSelf(a.proof?.method) : only === "inst" ? isInst(a.proof?.method) : true))
      .filter((a) => !n || `${a.name} ${(a.members || []).join(" ")}`.toLowerCase().includes(n))
      .sort((a, b) => sort.dir * String(col.get(a)).localeCompare(String(col.get(b)), "ko"));
  }, [data, sort, only, q]);
  return (
    <div>
      <div className="fta-filter" role="group" aria-label="발급방식 거르기">
        {[["all", "전체"], ["self", "자율발급 가능"], ["inst", "기관발급 가능"]].map(([k, l]) => (
          <button key={k} type="button" className={only === k ? "on" : ""} onClick={() => setOnly(k)}>{l}</button>
        ))}
        <span className="fta-filter-count">{rows.length}개 협정</span>
      </div>
      <div className="fta-table-wrap">
        <table className="fta-table" data-testid="fta-compare">
          <thead>
            <tr>
              {COLS.map((c) => (
                <th key={c.key}>
                  <button type="button" onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : 1 }))}>
                    {c.label}{sort.key === c.key ? (sort.dir > 0 ? " ▲" : " ▼") : ""}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id}>
                <td><a href={ftaHref({ ag: a.id })} onClick={(e) => { e.preventDefault(); go({ ag: a.id }); }}>{a.name}</a></td>
                <td className="nowrap">{fmtDate(a.dates?.effective)}</td>
                <td>{a.proof?.method}</td>
                <td>{a.proof?.issuer}</td>
                <td>{a.proof?.form}</td>
                <td className="nowrap">{a.proof?.validity}</td>
                <td>{a.proof?.language}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** 신고 문안의 빈칸(… 또는 .....)을 입력값으로 채운다. 순서: 인증번호 → 원산지. */
export function fillDeclaration(text, { authNo, origin }) {
  if (!text) return "";
  let t = text;
  // 인증수출자 번호가 없으면 괄호 문구를 뺀다 — 한·EU 원산지 의정서 부속서 III 각주 1
  if (!authNo) t = t.replace(/\s*\(customs authori[sz]ation No\s*(\.{3,}|…+)\s*(\(\d\))?\)/i, "");
  let i = authNo ? 0 : 1;
  return t
    .replace(/(\.{3,}|…+)\s*(\(\d\))?/g, (m, dots, mark, offset, whole) => {
      i += 1;
      const v = i === 1 ? authNo : i === 2 ? origin : null;
      if (!v) return m;
      // 'No...' · 'of...' 처럼 빈칸이 앞 낱말에 붙어 있으면 한 칸 띄운다. 각주 번호 (1)·(2)는 옮겨 적지 않는다
      return (offset > 0 && /\S/.test(whole[offset - 1]) ? " " : "") + v + (/\s$/.test(m) ? " " : "");
    })
    .replace(/\s+([.,])/g, "$1")
    .replace(/\s{2,}/g, " ");
}

function DeclarationMaker({ text }) {
  const [authNo, setAuthNo] = useState("");
  const [origin, setOrigin] = useState("Korean");
  const [place, setPlace] = useState("");
  const [signer, setSigner] = useState("");
  const [copied, setCopied] = useState(false);
  const body = fillDeclaration(text, { authNo: authNo.trim(), origin: origin.trim() });
  const full = [body, place.trim() && `${place.trim()}`, signer.trim() && `${signer.trim()}`].filter(Boolean).join("\n\n");
  const copy = () => {
    try {
      navigator.clipboard.writeText(full);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      /* 복사 권한이 없으면 사용자가 직접 선택해 복사한다 */
    }
  };
  return (
    <div className="fta-maker" data-testid="fta-maker">
      <div className="fta-maker-fields">
        <label>인증수출자 번호<input value={authNo} onChange={(e) => setAuthNo(e.target.value)} placeholder="예: 0000-00-000000" /></label>
        <label>원산지(영문)<input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="Korean" /></label>
        <label>장소·날짜<input value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Seoul, 2 October 2026" /></label>
        <label>서명자 이름<input value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="Hong Gildong" /></label>
      </div>
      <pre className="fta-maker-out">{full}</pre>
      <div className="fta-maker-foot">
        <button type="button" className="fta-btn" onClick={copy}>{copied ? "복사했습니다" : "문구 복사"}</button>
        <span>문구는 협정문 원문입니다. 인증번호를 쓰는 조건과 서명 방법은 협정 원산지 규정을 확인하세요.</span>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  if (children == null || children === false || children === "" || (Array.isArray(children) && children.length === 0)) return null;
  return (
    <div className="fta-row">
      <div className="fta-row-label">{label}</div>
      <div className="fta-row-value">{children}</div>
    </div>
  );
}

function Exemptions({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="fta-sec">
      <h3>6. 관세감면(FTA특례법 제30조)</h3>
      <p className="fta-note">협정에서 정한 일시수입·재수입 물품 등은 요건을 갖추면 관세가 면제됩니다. 신고서에는 아래 감면부호를 씁니다.</p>
      <div className="fta-table-wrap">
        <table className="fta-table" data-testid="fta-exemptions">
          <thead>
            <tr><th><span className="fta-th">대상 물품</span></th><th><span className="fta-th">관세 감면부호</span></th><th><span className="fta-th">부가세 감면부호</span></th></tr>
          </thead>
          <tbody>
            {items.map((x) => (
              <tr key={x.code}>
                <td>
                  {x.text || "—"}
                  {x.refs && x.refs.length > 0 && <div className="fta-muted">{x.refs.join(" · ")}{x.country ? ` · ${x.country}` : ""}</div>}
                </td>
                <td className="nowrap fta-code">{x.code}</td>
                <td className="nowrap fta-code">{x.vat_code || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── 세번에서 넘어온 경우(클에스 '세율 > 관세 > FTA협정관세 > 영국' 과 같은 길, 문의 #125 2026-10-02) ──
const fmtHsk = (h) => (h && String(h).length === 10 ? `${String(h).slice(0, 4)}.${String(h).slice(4, 6)}-${String(h).slice(6)}` : h || "");
const fmtYmd = (s) => (s ? String(s).replace(/(\d{4})(\d{2})(\d{2})/, "$1.$2.$3") : "");
export function quickrefHref(hsk) {
  const h = String(hsk || "").replace(/\D/g, "");
  return `/?audit=quickref&noextra=1&qhs=${h.slice(0, 4)}&hsk=${h}`;
}

function useHskDetail(hsk) {
  const [det, setDet] = useState(null); // null=불러오는 중, false=실패
  useEffect(() => {
    if (!hsk) return undefined;
    let alive = true;
    setDet(null);
    fetch(`/api/onto/hsk-detail?hsk=${encodeURIComponent(hsk)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => alive && setDet(d))
      .catch(() => alive && setDet(false));
    return () => {
      alive = false;
    };
  }, [hsk]);
  return det;
}

/** 그 세번에 붙은 이 협정의 세율 줄(선택1·선택2…) */
function ratesOf(det, a) {
  if (!det) return [];
  const all = Object.values(det.rates_grouped?.fta || {}).flat();
  return all.filter((r) => (a.rate_prefixes || []).some((p) => String(r.code || "").startsWith(p)));
}

function HsContext({ data, a, hsk, code, publicMode, tab, setTab, det }) {
  const [draft, setDraft] = useState(fmtHsk(hsk));
  useEffect(() => setDraft(fmtHsk(hsk)), [hsk]);
  const rows = ratesOf(det, a);
  const toHs = (e) => {
    if (publicMode) return;
    if (e && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)) return;
    if (e) e.preventDefault();
    window.history.pushState({}, "", quickrefHref(hsk));
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  const submit = (e) => {
    e.preventDefault();
    const h = draft.replace(/\D/g, "");
    if (h.length === 10) go({ ag: a.id, hsk: h });
  };
  return (
    <div className="fta-ctx" data-testid="fta-hs-context">
      <nav className="fta-crumb" aria-label="경로">
        {publicMode ? <span>세번 {fmtHsk(hsk)}</span> : <a href={quickrefHref(hsk)} onClick={toHs}>세번 {fmtHsk(hsk)}</a>}
        <span className="fta-crumb-sep">›</span>
        <a href={ftaHref({ hsk })} onClick={(e) => { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); go({ hsk }); }}>FTA 협정세율</a>
        <span className="fta-crumb-sep">›</span>
        <b>{a.name}</b>
        {!publicMode && (
          <a className="fta-crumb-back" href={quickrefHref(hsk)} onClick={toHs}>← 세번으로</a>
        )}
      </nav>
      <form className="fta-ctx-bar" onSubmit={submit}>
        <label>
          협정
          <select value={a.id} onChange={(e) => go({ ag: e.target.value, hsk })} aria-label="협정 바꾸기">
            {(data.agreements || []).map((x) => (
              <option key={x.id} value={x.id}>{x.name}</option>
            ))}
          </select>
        </label>
        <label>
          세번
          <input value={draft} onChange={(e) => setDraft(e.target.value)} inputMode="numeric" aria-label="세번" />
        </label>
        <button type="submit" className="fta-btn-ghost">보기</button>
      </form>
      <div className="fta-table-wrap">
        <table className="fta-table fta-ctx-table" data-testid="fta-hs-rates">
          <thead>
            <tr>
              <th><span className="fta-th">HS</span></th>
              <th><span className="fta-th">품명</span></th>
              <th><span className="fta-th">세율</span></th>
              <th><span className="fta-th">부호</span></th>
              <th><span className="fta-th">적용 기간</span></th>
            </tr>
          </thead>
          <tbody>
            {det === null ? (
              <tr><td colSpan={5} className="fta-muted">세번 정보를 불러오는 중…</td></tr>
            ) : det === false ? (
              <tr><td colSpan={5} className="fta-muted">세번 정보를 불러오지 못했습니다.</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="fta-muted">이 세번에는 {a.name} 협정세율이 없습니다.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.code} className={r.code === code ? "on" : ""}>
                  <td className="nowrap fta-code">{fmtHsk(hsk)}</td>
                  <td>{String(det.name_ko || "").replace(/\/양허유형;\d+$/, "")}</td>
                  <td className="nowrap fta-rate">{r.raw || (r.value != null ? `${r.value}%` : "—")}</td>
                  <td className="nowrap fta-code">{r.code}</td>
                  <td className="nowrap">{fmtYmd(r.strt)} ~ {fmtYmd(r.end)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="fta-tabs fta-ctx-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "info"} className={tab === "info" ? "on" : ""} onClick={() => setTab("info")}>협정 안내</button>
        <button type="button" role="tab" aria-selected={tab === "psr"} className={tab === "psr" ? "on" : ""} onClick={() => setTab("psr")}>원산지결정기준(이 세번)</button>
      </div>
    </div>
  );
}

/** 경로의 'FTA 협정세율' — 그 세번에 붙은 모든 협정의 세율을 한 표로(클에스 '세율 > 관세 > FTA협정관세' 자리, 2026-10-02). */
function HsRatesOverview({ data, hsk, publicMode }) {
  const det = useHskDetail(hsk);
  const rows = useMemo(() => {
    if (!det) return [];
    const all = Object.values(det.rates_grouped?.fta || {}).flat();
    return all
      .map((r) => {
        const id = agreementIdForRate(data, r.code);
        const a = id ? (data.agreements || []).find((x) => x.id === id) : null;
        return { r, a };
      })
      .sort((x, y) => (x.r.value ?? 999) - (y.r.value ?? 999) || String(x.r.code).localeCompare(String(y.r.code)));
  }, [det, data]);
  const toHs = (e) => {
    if (publicMode) return;
    if (e && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)) return;
    if (e) e.preventDefault();
    window.history.pushState({}, "", quickrefHref(hsk));
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  return (
    <div className="fta-ctx" data-testid="fta-hs-overview">
      <nav className="fta-crumb" aria-label="경로">
        {publicMode ? <span>세번 {fmtHsk(hsk)}</span> : <a href={quickrefHref(hsk)} onClick={toHs}>세번 {fmtHsk(hsk)}</a>}
        <span className="fta-crumb-sep">›</span>
        <b>FTA 협정세율</b>
        {!publicMode && <a className="fta-crumb-back" href={quickrefHref(hsk)} onClick={toHs}>← 세번으로</a>}
      </nav>
      <h2 className="fta-ov-title">
        {fmtHsk(hsk)} {det ? String(det.name_ko || "").replace(/\/양허유형;\d+$/, "") : ""}
      </h2>
      <div className="fta-table-wrap">
        <table className="fta-table" data-testid="fta-hs-overview-table">
          <thead>
            <tr>
              <th><span className="fta-th">협정</span></th>
              <th><span className="fta-th">부호</span></th>
              <th><span className="fta-th">세율</span></th>
              <th><span className="fta-th">적용 기간</span></th>
              <th><span className="fta-th">원산지증명</span></th>
            </tr>
          </thead>
          <tbody>
            {det === null ? (
              <tr><td colSpan={5} className="fta-muted">세번 정보를 불러오는 중…</td></tr>
            ) : det === false ? (
              <tr><td colSpan={5} className="fta-muted">세번 정보를 불러오지 못했습니다.</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} className="fta-muted">이 세번에는 FTA 협정세율이 없습니다.</td></tr>
            ) : (
              rows.map(({ r, a }) => (
                <tr key={r.code}>
                  <td>
                    {a ? (
                      <a href={ftaHref({ ag: a.id, hsk, code: r.code })} onClick={(e) => { e.preventDefault(); go({ ag: a.id, hsk, code: r.code }); }}>
                        {a.name}
                      </a>
                    ) : (
                      r.label || r.code
                    )}
                  </td>
                  <td className="nowrap fta-code">{r.code}</td>
                  <td className="nowrap fta-rate">{r.raw || (r.value != null ? `${r.value}%` : "—")}</td>
                  <td className="nowrap">{fmtYmd(r.strt)} ~ {fmtYmd(r.end)}</td>
                  <td>{a ? [a.proof?.method, a.proof?.validity ? `유효 ${a.proof.validity}` : null].filter(Boolean).join(" · ") : "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="fta-note">세율이 낮은 순서입니다. 협정 이름을 누르면 그 협정의 법령·증명 방식·신고 문구를 봅니다. 협정 요건(원산지증명·직접운송 등)을 갖춰야 적용됩니다.</p>
    </div>
  );
}

function PsrPanel({ a, hsk }) {
  const ag = useMemo(() => resolveFtaByCode(`${(a.rate_prefixes || [])[0] || ""}1`), [a]);
  const ftaId = ag && ag.ftaId;
  const hs6 = String(hsk || "").slice(0, 6);
  const [psr, setPsr] = useState(null);
  useEffect(() => {
    if (!ftaId || !hs6) return undefined;
    let alive = true;
    setPsr(null);
    fetch(`/api/onto/fta-psr?ftaId=${ftaId}&hs6=${hs6}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && setPsr(d || { available: false }))
      .catch(() => alive && setPsr({ available: false }));
    return () => {
      alive = false;
    };
  }, [ftaId, hs6]);
  const portal = ftaId
    ? `https://www.customs.go.kr/ftaportalkor/ad/ftaTrtyPsr/psrCategoryView.do?ftaId=${ftaId}&nationId=&searchType=01&searchValue=${hs6}`
    : a.links?.kcs_psr;
  const rows = psr && psr.available ? (psr.rows && psr.rows.length ? psr.rows : [{ item_ko: psr.name_ko, criterion_ko: psr.psr_ko, criterion_en: psr.psr_en }]) : [];
  return (
    <section className="fta-sec" data-testid="fta-psr">
      <h3>원산지결정기준 — 제{hs6.slice(0, 4)}.{hs6.slice(4, 6)}호</h3>
      {!ftaId ? (
        <p className="fta-note">이 협정은 관세청 FTA 포털에서 바로 조회합니다.</p>
      ) : psr === null ? (
        <p className="fta-note">관세청 FTA 포털에서 불러오는 중…</p>
      ) : rows.length === 0 ? (
        <p className="fta-note">관세청 FTA 포털에 이 세번의 원산지결정기준이 게시되어 있지 않습니다.</p>
      ) : (
        rows.map((r, i) => (
          <div key={i} className="fta-psr-row">
            {rows.length > 1 && <div className="fta-muted">선택 {r.division || i + 1}</div>}
            {r.item_ko && <div className="fta-muted">{r.item_ko}</div>}
            <div>{r.criterion_ko || "기준 본문 없음"}</div>
            {r.criterion_en && <div className="fta-muted" lang="en">{r.criterion_en}</div>}
          </div>
        ))
      )}
      {portal && (
        <p className="fta-note"><a href={portal} target="_blank" rel="noopener noreferrer">관세청 FTA 포털 원문 ↗</a></p>
      )}
    </section>
  );
}

function AgreementDetail({ data, a, publicMode = false, hsk = null, code = null }) {
  const d = a.declaration || {};
  const decl = d.en;
  const [tab, setTab] = useState("info");
  useEffect(() => setTab("info"), [a.id, hsk]);
  const det = useHskDetail(hsk);
  return (
    <article className="fta-detail" data-testid="fta-detail">
      {hsk ? (
        <HsContext data={data} a={a} hsk={hsk} code={code} publicMode={publicMode} tab={tab} setTab={setTab} det={det} />
      ) : (
        <button type="button" className="fta-back" onClick={() => go({})}>← 협정 목록</button>
      )}
      {hsk && tab === "psr" ? (
        <PsrPanel a={a} hsk={hsk} />
      ) : (
      <>
      <header className="fta-detail-head">
        <h2>{a.name}</h2>
        <div className="fta-detail-sub">
          {fmtDate(a.dates?.effective)} 발효 · {membersShort(a)} · 세율부호 {(a.rate_prefixes || []).join(", ")}
        </div>
      </header>

      <section className="fta-sec">
        <h3>1. 적용 법령</h3>
        <ul className="fta-list">
          {(a.treaties || []).map((t) => (
            <li key={t.id}><a href={t.url} target="_blank" rel="noopener noreferrer">협정문(국가법령정보센터 조약) ↗</a></li>
          ))}
          {a.links?.motir_texts && (
            <li><a href={a.links.motir_texts} target="_blank" rel="noopener noreferrer">협정문 장별 보기(산업통상부) ↗</a></li>
          )}
          {(data.laws || []).map((l) => (
            <li key={l.url}><a href={encodeURI(l.url)} target="_blank" rel="noopener noreferrer">{l.name}</a> <span className="fta-muted">{l.kind}</span></li>
          ))}
          <li><a href={a.links?.kcs_psr} target="_blank" rel="noopener noreferrer">품목별 원산지결정기준(관세청 FTA 포털) ↗</a></li>
        </ul>
      </section>

      <section className="fta-sec">
        <h3>2. 적용 대상국과 발효</h3>
        <Row label="협상 개시">{fmtDate(a.dates?.negotiation_started)}</Row>
        <Row label="서명">{fmtDate(a.dates?.signed)}</Row>
        <Row label="발효">
          {fmtDate(a.dates?.effective)}
          {/* 원본 표가 단계(잠정·전체, 국가별 순차)를 함께 적은 경우 그 문구를 그대로 보인다 */}
          {a.stages?.[0]?.effective && /[(*]/.test(a.stages[0].effective) && (
            <div className="fta-muted">{a.stages[0].effective}</div>
          )}
        </Row>
        {a.stages && a.stages.length > 1 && (
          <Row label="단계">
            <ul className="fta-list">
              {a.stages.map((s, i) => (
                <li key={i}>서명 {s.signed || "—"} · 발효 {s.effective || "—"}</li>
              ))}
            </ul>
          </Row>
        )}
        <Row label="대상국">{(a.members || []).join(", ")}</Row>
        {(a.member_notes || []).map((n, i) => (
          <p key={i} className="fta-note">{n}</p>
        ))}
      </section>

      <section className="fta-sec">
        <h3>3. 원산지증명서 발급 방식</h3>
        <Row label="증명서 종류">{(a.proof?.kinds || []).join(", ")}</Row>
        <Row label="발급방식"><MethodBadges method={a.proof?.method} /> {a.proof?.method}</Row>
        <Row label="발급자">{a.proof?.issuer}</Row>
        <Row label="서식">{a.proof?.form}</Row>
        <Row label="유효기간">{a.proof?.validity}</Row>
        <Row label="사용언어">{a.proof?.language}</Row>
        <Row label="사용횟수">{a.proof?.use}</Row>
        <Row label="금액 기준">
          {(d.waivers || []).length > 0 && (
            <ul className="fta-list">{d.waivers.map((w) => <li key={w}>{w}</li>)}</ul>
          )}
        </Row>
        {(a.treaty_notes || []).length > 0 && (
          <div className="fta-callout" data-testid="fta-treaty-notes">
            <div className="fta-callout-title">알아 둘 점 — 협정 개정</div>
            {a.treaty_notes.map((n) => <div key={n.text}>{n.text}</div>)}
            <div className="fta-muted"><a href={a.treaty_notes[0].url} target="_blank" rel="noopener noreferrer">{a.treaty_notes[0].source} ↗</a></div>
          </div>
        )}
        {(a.notes || []).length > 0 && (
          <div className="fta-callout" data-testid="fta-notes">
            <div className="fta-callout-title">관세청 안내</div>
            {a.notes.map((n, i) => <div key={i}>{n}</div>)}
          </div>
        )}
      </section>

      {(decl || d.ko || (d.elements || []).length > 0) && (
        <section className="fta-sec">
          <h3>{decl || d.ko ? "4. 원산지 신고서 문구" : "4. 원산지증명서에 적을 것"}</h3>
          {decl && <blockquote className="fta-quote" lang="en">{decl}</blockquote>}
          {d.ko && <blockquote className="fta-quote" lang="ko">{d.ko}</blockquote>}
          {(d.lines || []).length > 0 && (
            <div className="fta-lines" lang="en">{d.lines.map((l) => <div key={l}>…………………… {l}</div>)}</div>
          )}
          {(d.footnotes || []).length > 0 && (
            <details className="fta-foot" data-testid="fta-footnotes">
              <summary>협정문 각주 {d.footnotes.length}개 — 인증번호·원산지·서명 적는 법</summary>
              {d.footnotes.map((f) => <p key={f} lang={/^[(\d]/.test(f) && /[a-z]/i.test(f) ? "en" : "ko"}>{f}</p>)}
            </details>
          )}
          {d.source && (
            <p className="fta-note">
              문구 출처: <a href={d.source.url} target="_blank" rel="noopener noreferrer">{d.source.name} ↗</a>
            </p>
          )}
          {(d.elements || []).length > 0 && (
            <ul className="fta-list fta-elements" data-testid="fta-elements">{d.elements.map((e) => <li key={e}>{e}</li>)}</ul>
          )}
          {decl && !publicMode && (
            <>
              <h4>문구 만들기</h4>
              <DeclarationMaker text={decl} />
            </>
          )}
          {decl && publicMode && (
            <p className="fta-note">로그인하면 인증번호·원산지를 넣어 문구를 완성하고 복사할 수 있습니다.</p>
          )}
        </section>
      )}

      {(a.forms || []).length > 0 && (
        <section className="fta-sec">
          <h3>5. 서식 내려받기</h3>
          <ul className="fta-list fta-forms" data-testid="fta-forms">
            {a.forms.map((f) => (
              <li key={f.title}>
                <span>{f.title}</span>
                {f.hwp && <a href={f.hwp} target="_blank" rel="noopener noreferrer">HWP</a>}
                {f.pdf && <a href={f.pdf} target="_blank" rel="noopener noreferrer">PDF</a>}
              </li>
            ))}
          </ul>
          <p className="fta-note">관세청 FTA 포털의 서식 파일로 연결됩니다.</p>
        </section>
      )}

      <Exemptions items={a.exemptions} />
      </>
      )}


      <Sources data={data} />
    </article>
  );
}

function Sources({ data }) {
  const latest = (data.sources || []).map((s) => s.checked_at || s.fetched_at).filter(Boolean).sort().pop();
  return (
    <footer className="fta-sources">
      출처: {[
        ["관세청 FTA 포털", (data.sources || []).find((s) => s.url && s.key.startsWith("kcs"))],
        ["산업통상부 FTA", (data.sources || []).find((s) => s.url && !s.key.startsWith("kcs"))],
      ].filter(([, s]) => s).map(([label, s], i) => (
        <span key={label}>{i ? " · " : ""}<a href={s.url} target="_blank" rel="noopener noreferrer">{label}</a></span>
      ))}
      {latest ? ` · 확인 ${latest.slice(0, 10)}` : ""}. 참고 자료이며, 신고 전 협정문과 관세청 자료를 확인하세요.
    </footer>
  );
}

export default function FtaCenterPage({ active = true, publicMode = false }) {
  setBase(publicMode);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [route, setRoute] = useState(readRoute);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    let alive = true;
    loadFtaCenter().then((d) => alive && setData(d)).catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    // keep-alive 화면 — 내 주소(audit=fta)일 때만 읽는다(다른 화면의 뒤로 가기에 흔들리지 않게)
    const h = () => {
      if (new URLSearchParams(window.location.search).get(BASE.key) === "fta") setRoute(readRoute());
    };
    window.addEventListener("popstate", h);
    return () => window.removeEventListener("popstate", h);
  }, []);
  useEffect(() => { if (active) setRoute(readRoute()); }, [active]);
  useEffect(() => {
    // 협정을 열거나 목록으로 돌아오면 맨 위부터 — 이 화면을 감싼 스크롤 상자를 올린다
    let el = rootRef.current && rootRef.current.parentElement;
    while (el && el !== document.body) {
      if (el.scrollHeight > el.clientHeight && /(auto|scroll)/.test(getComputedStyle(el).overflowY)) {
        el.scrollTop = 0;
        break;
      }
      el = el.parentElement;
    }
  }, [route.ag, route.code, route.hsk]);

  if (err) return <div className="fta-page audit-root" ref={rootRef}><p className="fta-empty">FTA 자료를 불러오지 못했습니다. 잠시 뒤 다시 열어 주세요.</p></div>;
  if (!data) return <div className="fta-page audit-root" ref={rootRef}><p className="fta-empty">불러오는 중…</p></div>;

  const agId = route.ag || (route.code ? agreementIdForRate(data, route.code) : null);
  const a = agId ? (data.agreements || []).find((x) => x.id === agId) : null;
  return (
    <div className="fta-page audit-root" ref={rootRef}>
      {!a && route.hsk ? (
        <HsRatesOverview data={data} hsk={route.hsk} publicMode={publicMode} />
      ) : a ? (
        <AgreementDetail data={data} a={a} publicMode={publicMode} hsk={route.hsk} code={route.code} />
      ) : (
        <>
          <header className="fta-head">
            <h1>FTA 센터</h1>
            <p>발효 중인 {data.agreements?.length || 0}개 협정의 대상국·발효일·원산지증명 방식을 한곳에서 봅니다.</p>
          </header>
          <div className="fta-toolbar">
            <div className="fta-tabs" role="tablist">
              <button type="button" role="tab" aria-selected={route.view === "list"} className={route.view === "list" ? "on" : ""} onClick={() => go({ view: "list" })}>협정 목록</button>
              <button type="button" role="tab" aria-selected={route.view === "compare"} className={route.view === "compare" ? "on" : ""} onClick={() => go({ view: "compare" })}>한눈에 비교</button>
            </div>
            <input className="fta-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="협정·나라·세율부호 찾기" aria-label="협정 찾기" />
          </div>
          {route.view === "compare" ? <CompareTable data={data} q={q} /> : <AgreementList data={data} q={q} />}
          {(data.pending || []).length > 0 && (
            <section className="fta-pending" data-testid="fta-pending">
              {data.pending.map((p) => (
                <div key={p.kind} className="fta-pending-row">
                  <span className="fta-pending-title">{p.kind === "signed" ? "서명·타결" : "협상 중"}</span>
                  <span className="fta-pending-names">{p.names.join(" · ")}</span>
                </div>
              ))}
              <div className="fta-muted">아직 발효 전이라 협정세율을 쓸 수 없습니다. 출처: 관세청 FTA 포털 'FTA 협상현황'.</div>
            </section>
          )}
          <Sources data={data} />
        </>
      )}
    </div>
  );
}
